/**
 * The commission side of the programme: every partner's ledger, adjustments
 * waiting for a second person, reconciliation, discount codes, fraud alerts
 * and the programme's payout rules.
 *
 * Reconciliation reports; it never corrects. An adjustment is approved by
 * somebody other than whoever asked for it — the server refuses otherwise, and
 * this screen does not offer it.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import {
  Alert,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Input,
  LoadingState,
  PageHeader,
  Select,
  ServerTable,
  StatCard,
  StatRow,
  Tabs,
  Textarea,
  createServerFetcher,
  formatDate,
  formatDateTime,
  formatMoney,
  humanize,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import {
  LINE_KIND_LABELS,
  SERVICE_LABELS,
  partnerKeys,
  partnersApi,
  percent,
  type Adjustment,
  type LedgerRow,
  type ProgramRules,
} from './api';
import { PlanEditor, planFrom, type PayoutPlan } from './plan-editor';
import { Failed, ReasonDialog, errorText } from './shared';

type Tab = 'ledger' | 'adjustments' | 'reconciliation' | 'codes' | 'alerts' | 'rules';

export function PartnerCommissionsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'ledger';
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Commissions"
        description="Every partner's commission ledger, adjustments, reconciliation, codes, alerts and the programme's rules."
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            items={[
              { id: 'ledger', label: 'Ledger' },
              { id: 'adjustments', label: 'Adjustments' },
              { id: 'reconciliation', label: 'Reconciliation' },
              { id: 'codes', label: 'Discount codes' },
              { id: 'alerts', label: 'Alerts' },
              { id: 'rules', label: 'Programme rules' },
            ]}
          />
        }
      />
      {tab === 'ledger' ? (
        <Ledger />
      ) : tab === 'adjustments' ? (
        <Adjustments />
      ) : tab === 'reconciliation' ? (
        <Reconciliation />
      ) : tab === 'codes' ? (
        <Codes />
      ) : tab === 'alerts' ? (
        <Alerts />
      ) : (
        <Rules />
      )}
    </div>
  );
}

function Ledger() {
  const fetchRows = useMemo(() => createServerFetcher<LedgerRow>('/partners/ledger'), []);
  const columns = useMemo<ServerColumn<LedgerRow>[]>(
    () => [
      { field: 'reference', header: 'Reference', filter: 'text', width: 140 },
      { field: 'at', header: 'Recorded', filter: 'date', width: 120, format: (v) => formatDate(v as string) },
      { field: 'client', header: 'Customer', filter: 'text', minWidth: 160 },
      {
        field: 'kind',
        header: 'Type',
        filter: 'select',
        options: Object.entries(LINE_KIND_LABELS).map(([value, label]) => ({ value, label })),
        width: 120,
        format: (v) => LINE_KIND_LABELS[v as LedgerRow['kind']],
      },
      {
        field: 'service',
        header: 'Service',
        filter: 'select',
        options: Object.entries(SERVICE_LABELS).map(([value, label]) => ({ value, label })),
        width: 130,
        format: (v) => (v ? SERVICE_LABELS[v as keyof typeof SERVICE_LABELS] : '—'),
      },
      { field: 'eligible_amount', header: 'Eligible', filter: 'number', numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'rate', header: 'Rate', sortable: false, width: 80, format: (v) => percent(v as string | null) },
      { field: 'discount_amount', header: 'Discount', sortable: false, numeric: true, width: 110, format: (v) => formatMoney(v as string) },
      { field: 'amount', header: 'Amount', filter: 'number', numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'status', header: 'Status', sortable: false, width: 110, format: (v) => humanize(v as string) },
    ],
    [],
  );
  return (
    <ServerTable<LedgerRow>
      columns={columns}
      fetchRows={fetchRows}
      rowKey={(row) => row.public_id}
      queryKey={partnerKeys.ledger()}
      stateKey="partners.ledger"
      searchPlaceholder="Search by customer or reference"
      rowDetailFields={(row) => [
        { label: 'What', value: row.description },
        { label: 'Payable from', value: formatDateTime(row.matures_at) },
        { label: 'Computed from', value: JSON.stringify(row.inputs ?? {}) },
      ]}
      emptyTitle="No commission yet"
      emptyDescription="Commission is read from attributed customers' wallet spending on every sweep, or with Run now under Reconciliation."
    />
  );
}

function Adjustments() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: partnerKeys.adjustments, queryFn: () => partnersApi.adjustments() });
  const [deciding, setDeciding] = useState<{ adjustment: Adjustment; approve: boolean } | null>(null);
  const decide = useMutation({
    mutationFn: (note: string) => partnersApi.decideAdjustment(deciding!.adjustment.public_id, deciding!.approve, note),
    onSuccess: async () => {
      toast.success(deciding?.approve ? 'Adjustment posted' : 'Adjustment rejected');
      setDeciding(null);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
  if (list.isPending) return <LoadingState />;
  if (list.isError) return <Failed error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <Card>
      <CardHeader
        title="Adjustments"
        description="Corrections to a partner's commission. Asked for on the partner's page; approved here by somebody else."
      />
      <CardBody className="divide-y divide-line">
        {list.data.length === 0 ? <p className="text-sm text-muted">No adjustments.</p> : null}
        {list.data.map((a) => (
          <div key={a.public_id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
            <div>
              <p className="font-medium">
                {a.partner} · {formatMoney(a.amount)}
              </p>
              <p className="text-muted">{a.reason}</p>
              <p className="text-xs text-faint">
                {formatDateTime(a.at)} · {humanize(a.status)}
                {a.decision_note ? ` · ${a.decision_note}` : ''}
              </p>
            </div>
            {a.status === 'pending' ? (
              a.requested_by_me ? (
                <span className="text-xs text-muted">Asked for by you — somebody else approves it.</span>
              ) : (
                <Can permission="partners.ledger.approve">
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setDeciding({ adjustment: a, approve: true })}>
                      Approve
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setDeciding({ adjustment: a, approve: false })}>
                      Reject
                    </Button>
                  </div>
                </Can>
              )
            ) : null}
          </div>
        ))}
      </CardBody>
      <ReasonDialog
        open={deciding !== null}
        title={deciding?.approve ? 'Approve and post this adjustment' : 'Reject this adjustment'}
        description={deciding ? `${deciding.adjustment.partner}: ${formatMoney(deciding.adjustment.amount)}` : undefined}
        confirmLabel={deciding?.approve ? 'Approve' : 'Reject'}
        variant={deciding?.approve ? 'primary' : 'danger'}
        minLength={0}
        busy={decide.isPending}
        error={decide.error}
        onClose={() => setDeciding(null)}
        onConfirm={(note) => decide.mutate(note)}
      />
    </Card>
  );
}

function Reconciliation() {
  const queryClient = useQueryClient();
  const report = useQuery({ queryKey: partnerKeys.reconciliation, queryFn: partnersApi.reconciliation });
  const run = useMutation({
    mutationFn: partnersApi.runAccrual,
    onSuccess: async (done) => {
      toast.success('Commission run finished', `${done.read} wallet entries read, ${done.entries} ledger lines posted.`);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
    onError: (e) => toast.error('The run failed', errorText(e)),
  });
  if (report.isPending) return <LoadingState />;
  if (report.isError) return <Failed error={report.error} onRetry={() => void report.refetch()} />;
  const r = report.data;
  const clean =
    r.ledger_entries_not_matching_sources === 0 && r.payouts_not_adding_up.length === 0 && r.payouts_open_over_a_week.length === 0;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="partners.ledger.create">
          <Button loading={run.isPending} onClick={() => run.mutate()}>
            <Play className="size-4" />
            Run now
          </Button>
        </Can>
      </div>
      {clean ? (
        <Alert tone="success" title="The books agree">
          Every ledger line matches the wallet entries it was computed from, and every payout adds up.
        </Alert>
      ) : (
        <Alert tone="warning" title="Something does not add up">
          It is reported, never corrected: look at the items below.
        </Alert>
      )}
      <StatRow>
        <StatCard
          label="Wallet entries not yet read"
          value={r.wallet_entries_not_yet_read}
          hint={`Entries newer than ${r.settle_minutes} minutes wait for the next run`}
        />
        <StatCard label="Ledger lines not matching" value={r.ledger_entries_not_matching_sources} tone={r.ledger_entries_not_matching_sources ? 'danger' : 'neutral'} />
        <StatCard label="Payouts not adding up" value={r.payouts_not_adding_up.length} tone={r.payouts_not_adding_up.length ? 'danger' : 'neutral'} />
        <StatCard label="Open fraud alerts" value={r.open_fraud_alerts} tone={r.open_fraud_alerts ? 'warning' : 'neutral'} />
      </StatRow>
      {r.payouts_not_adding_up.length || r.payouts_open_over_a_week.length ? (
        <Card>
          <CardBody className="space-y-1 text-sm">
            {r.payouts_not_adding_up.length ? <p>Not adding up: {r.payouts_not_adding_up.join(', ')}</p> : null}
            {r.payouts_open_over_a_week.length ? <p>Open over a week: {r.payouts_open_over_a_week.join(', ')}</p> : null}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

interface CodeRow {
  public_id: string;
  code: string;
  partner: string;
  label: string;
  discount_rate: string;
  used: number;
  max_redemptions: number | null;
  status: string;
  expires_at: string | null;
  created_at: string;
}

function Codes() {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<CodeRow | null>(null);
  const fetchRows = useMemo(() => createServerFetcher<CodeRow>('/partners/codes'), []);
  const revoke = useMutation({
    mutationFn: (reason: string) => partnersApi.revokeCode(revoking!.public_id, reason),
    onSuccess: async () => {
      setRevoking(null);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.codes });
    },
  });
  const columns = useMemo<ServerColumn<CodeRow>[]>(
    () => [
      { field: 'code', header: 'Code', filter: 'text', width: 190, render: (row) => <span className="font-mono">{row.code}</span> },
      { field: 'partner', header: 'Partner', filter: 'text', minWidth: 170 },
      { field: 'label', header: 'Campaign', sortable: false, minWidth: 140 },
      { field: 'discount_rate', header: 'Discount', sortable: false, width: 100, format: (v) => percent(v as string) },
      {
        field: 'used',
        header: 'Used',
        sortable: false,
        width: 90,
        render: (row) => (row.max_redemptions ? `${row.used} / ${row.max_redemptions}` : row.used),
      },
      { field: 'status', header: 'Status', filter: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'revoked', label: 'Revoked' }], width: 110, format: (v) => humanize(v as string) },
      { field: 'created_at', header: 'Made', filter: 'date', width: 120, format: (v) => formatDate(v as string) },
    ],
    [],
  );
  return (
    <>
      <ServerTable<CodeRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.codes}
        stateKey="partners.codes"
        searchPlaceholder="Search by code or partner"
        rowActions={(row) =>
          row.status === 'revoked' ? [] : [{ label: 'Revoke', destructive: true, onSelect: () => setRevoking(row) }]
        }
        emptyTitle="No codes yet"
      />
      <ReasonDialog
        open={revoking !== null}
        title={`Revoke ${revoking?.code ?? ''}`}
        description="It stops working at once. Purchases already made with it keep their discount."
        confirmLabel="Revoke"
        variant="danger"
        busy={revoke.isPending}
        error={revoke.error}
        onClose={() => setRevoking(null)}
        onConfirm={(reason) => revoke.mutate(reason)}
      />
    </>
  );
}

function Alerts() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: partnerKeys.alerts, queryFn: () => partnersApi.alerts() });
  const [closing, setClosing] = useState<string | null>(null);
  const close = useMutation({
    mutationFn: (note: string) => partnersApi.closeAlert(closing!, note),
    onSuccess: async () => {
      setClosing(null);
      await queryClient.invalidateQueries({ queryKey: partnerKeys.alerts });
    },
  });
  if (list.isPending) return <LoadingState />;
  if (list.isError) return <Failed error={list.error} onRetry={() => void list.refetch()} />;
  return (
    <Card>
      <CardHeader
        title="Fraud alerts"
        description="Self-referral attempts and a customer trying code after code. Recorded automatically; closed by a person with a note."
      />
      <CardBody className="divide-y divide-line">
        {list.data.length === 0 ? <p className="text-sm text-muted">No alerts.</p> : null}
        {list.data.map((a) => (
          <div key={a.public_id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
            <div>
              <p className="font-medium">
                {humanize(a.kind)} {a.partner ? `· ${a.partner}` : ''} {a.client ? `· ${a.client}` : ''}
              </p>
              <p className="text-xs text-faint">
                {formatDateTime(a.at)} · {a.status}
                {a.note ? ` · ${a.note}` : ''}
              </p>
            </div>
            {a.status === 'open' ? (
              <Can permission="partners.partner.edit">
                <Button size="sm" variant="secondary" onClick={() => setClosing(a.public_id)}>
                  Close
                </Button>
              </Can>
            ) : null}
          </div>
        ))}
      </CardBody>
      <ReasonDialog
        open={closing !== null}
        title="Close this alert"
        description="Say what you found."
        confirmLabel="Close alert"
        busy={close.isPending}
        error={close.error}
        onClose={() => setClosing(null)}
        onConfirm={(note) => close.mutate(note)}
      />
    </Card>
  );
}

function Rules() {
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: partnerKeys.program, queryFn: partnersApi.program });
  if (rules.isPending) return <LoadingState />;
  if (rules.isError) return <Failed error={rules.error} onRetry={() => void rules.refetch()} />;
  return <RulesForm initial={rules.data} onSaved={() => void queryClient.invalidateQueries({ queryKey: partnerKeys.program })} />;
}

function RulesForm({ initial, onSaved }: { initial: ProgramRules; onSaved: () => void }) {
  const [plan, setPlan] = useState<PayoutPlan>(planFrom(initial.plan));
  const [weekend, setWeekend] = useState(initial.calendar.weekend);
  const [holidays, setHolidays] = useState(initial.calendar.holidays.join('\n'));
  const [noticeDays, setNoticeDays] = useState(String(initial.notice_bank_days));
  const [noticeTo, setNoticeTo] = useState(initial.notice_to.join(', '));
  const [secondOver, setSecondOver] = useState(initial.second_approval_over ?? '');
  const [settle, setSettle] = useState(String(initial.settle_minutes));
  const save = useMutation({
    mutationFn: () =>
      partnersApi.updateProgram({
        plan,
        calendar: {
          weekend,
          holidays: holidays
            .split(/[\s,]+/)
            .map((d) => d.trim())
            .filter(Boolean),
        },
        notice_bank_days: Number.parseInt(noticeDays || '0', 10),
        notice_to: noticeTo
          .split(/[\s,;]+/)
          .map((a) => a.trim())
          .filter(Boolean),
        second_approval_over: secondOver || null,
        settle_minutes: Number.parseInt(settle || '10', 10),
      }),
    onSuccess: () => {
      toast.success('Programme rules saved');
      onSaved();
    },
  });
  return (
    <div className="space-y-4">
      {save.isError ? <Alert tone="danger">{errorText(save.error)}</Alert> : null}
      <Card>
        <CardHeader
          title="How partners are paid, by default"
          description="Every partner without a plan of its own. A partner's page can give it its own plan or hold its payouts."
        />
        <CardBody>
          <PlanEditor value={plan} onChange={setPlan} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Runs and the bank calendar"
          description="A scheduled payout date's payouts are prepared this many bank working days before it, and the people below are emailed its sheet (accounts masked). The bank file is downloaded in Partner payouts."
        />
        <CardBody className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Input label="Notice (bank working days)" inputMode="numeric" value={noticeDays} onChange={(e) => setNoticeDays(e.target.value.replace(/\D/g, ''))} />
            <Select
              label="Banks are closed on"
              value={weekend}
              onChange={(e) => setWeekend(e.target.value as ProgramRules['calendar']['weekend'])}
              options={[
                { value: 'indian_banks', label: 'Sundays, 2nd and 4th Saturdays (India)' },
                { value: 'saturday_sunday', label: 'Saturdays and Sundays' },
                { value: 'sunday', label: 'Sundays' },
              ]}
            />
            <Input label="Second approval above ₹" hint="Empty: one approval is enough" inputMode="decimal" value={secondOver} onChange={(e) => setSecondOver(e.target.value.replace(/[^0-9.]/g, ''))} />
          </div>
          <Input
            label="Who is emailed each run"
            hint="Email addresses, separated by commas"
            value={noticeTo}
            onChange={(e) => setNoticeTo(e.target.value)}
          />
          {initial.notice_to.length === 0 ? <Alert tone="warning">Nobody is emailed until an address is set.</Alert> : null}
          <Textarea
            label="Bank holidays"
            hint="One date a line, YYYY-MM-DD. A payout date on a holiday moves to the next bank working day."
            rows={4}
            value={holidays}
            onChange={(e) => setHolidays(e.target.value)}
          />
          <Input label="Settle lag (minutes)" hint="How old a wallet entry must be before commission is read from it" inputMode="numeric" value={settle} onChange={(e) => setSettle(e.target.value.replace(/\D/g, ''))} />
        </CardBody>
      </Card>
      <Can permission="partners.ledger.approve">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Save programme rules
        </Button>
      </Can>
    </div>
  );
}
